import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SimpleGrid } from '@mantine/core';

import { useCreditRequestForm } from 'context';
import { AppConstants, RouteConstants, ServiceConstants } from '../../../constants';
import { SectionTitle, Text } from 'components';
import CreditRequestInfoData from '../info';
import { AppUtils } from 'utils';
import { CreditService } from 'services';
import { IRootState } from 'config/redux';
import { ICreditAttachment } from 'types';
import RequiredDocumentation from './required';
import NewDocumentation from './new';

const { CREDIT_REQUEST_STEP_DOCUMENTS } = AppConstants;
const { ROUTE_CREDIT_REQUEST_OVERVIEW } = RouteConstants;
const { REQUEST_CREDIT_REQUEST_ID, REQUEST_CREDIT_ATTACHMENTS_ID, REQUEST_CREDIT_ATTACHMENTS_UPLOAD_ID } = ServiceConstants;

const CreditRequestDocumentationPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cid = searchParams.get('cid');
  const { setActiveStep, currentCredit, setCurrentCredit, isEditable, setIsEditable } = useCreditRequestForm();
  const getServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_REQUEST_ID]);
  const attachmentsServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_ATTACHMENTS_ID]);
  const uploadServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_ATTACHMENTS_UPLOAD_ID]);

  const [requiredAttachments, setRequiredAttachments] = useState<ICreditAttachment[] | undefined>(undefined);
  const [deliveredAttachments, setDeliveredAttachments] = useState<any[]>([]);

  useEffect(() => {
    setActiveStep(CREDIT_REQUEST_STEP_DOCUMENTS);
    if (AppUtils.isEmpty(currentCredit) && !AppUtils.isEmpty(cid)) {
      CreditService.getRequest(cid!);
    } else if (!AppUtils.isEmpty(currentCredit)) {
      if (currentCredit.requestedAt && currentCredit.status !== 'PROCESSING') {
        setIsEditable(false);
      } else {
        setIsEditable(true);
      }
    }
    CreditService.getAttachments(cid!);
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      getServiceObject,
      REQUEST_CREDIT_REQUEST_ID,
      (val) => {
        if (val.requestedAt && val.status !== 'PROCESSING') {
          setIsEditable(false);
        }
        setCurrentCredit({
          id: val.id,
          creditAmount: val.creditAmount,
          paymentPeriod: val.paymentPeriod,
          monthlyInstallmentTo: val.monthlyInstallmentTo,
          status: val.status,
          requestedAt: val.requestedAt,
          user: val.user,
        });
      }
    );
  }, [getServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      attachmentsServiceObject,
      REQUEST_CREDIT_ATTACHMENTS_ID,
      (val) => {
        setRequiredAttachments(val.types);
        setDeliveredAttachments(val.attachments);
      }
    );
  }, [attachmentsServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      uploadServiceObject,
      REQUEST_CREDIT_ATTACHMENTS_UPLOAD_ID,
      () => {
        navigate(`${ROUTE_CREDIT_REQUEST_OVERVIEW}${location.search}`);
      }
    );
  }, [uploadServiceObject]);

  return (
    <>
      <CreditRequestInfoData
        creditAmount={currentCredit.creditAmount}
        paymentPeriod={currentCredit.paymentPeriod}
        monthlyInstallmentTo={currentCredit.monthlyInstallmentTo}
        t={t}
      />
      <SectionTitle title={t(`credit.step.${CREDIT_REQUEST_STEP_DOCUMENTS}.title`)} variant={'dark'} pt={'xl'} />
      <Text pb={'xl'} c={'gray'}>
        {t(`credit.step.${CREDIT_REQUEST_STEP_DOCUMENTS}.description.0`)}
        <span style={{ color: 'green', fontWeight: 'bold' }}>{t(`credit.step.${CREDIT_REQUEST_STEP_DOCUMENTS}.description.1`)}</span>
        {t(`credit.step.${CREDIT_REQUEST_STEP_DOCUMENTS}.description.2`, { email: currentCredit.user?.email })}
      </Text>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <RequiredDocumentation requiredAttachments={requiredAttachments} t={t} />
        <NewDocumentation
          cid={cid!}
          creditRequestId={currentCredit.id!}
          deliveredAttachments={deliveredAttachments}
          setDeliveredAttachments={setDeliveredAttachments}
          isEditable={isEditable}
          navigate={navigate}
          location={location}
          t={t}
        />
      </SimpleGrid>
    </>
  )
}

export default CreditRequestDocumentationPage;